import { ref } from 'vue';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toastService } from 'src/design_system/services/toast';
import {
  isConnectionError,
  getConnectionErrorKey,
} from 'src/utils/api/connectionErrorDetector';
import { useAppStore } from 'src/core/stores/app';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';

export function useRequestTracking(slowRequestThreshold = 10000) {
  const activeRequests = ref(new Map<string, number>());
  const slowRequestToastId = ref<string | null>(null);
  const slowRequestTimers = ref(new Map<string, NodeJS.Timeout>());
  const { handleError } = useErrorHandling();

  /**
   * Tracks the start of a request and sets up slow request detection
   */
  const trackRequestStart = (config: AxiosRequestConfig) => {
    const requestId = `${config.method}-${config.url}`;
    activeRequests.value.set(requestId, Date.now());

    // Set up slow request detection
    const timer = setTimeout(() => {
      if (activeRequests.value.has(requestId)) {
        const appStore = useAppStore();
        // Show toast only if we haven't already shown one AND there's no fatal error
        if (!slowRequestToastId.value && !appStore.errorDetails) {
          const message = appStore.t
            ? appStore.t('app.request_tracking.errors.connection.slow_request')
            : 'Requests are taking longer than usual...';
          const detail = appStore.t
            ? appStore.t(
                'app.request_tracking.errors.connection.slow_request_detail'
              )
            : 'Please wait while we process your request.';

          slowRequestToastId.value = toastService.warning(message, detail, {
            duration: 10000, // Auto-dismiss after 10 seconds
            position: 'top-right',
            close: false,
          });
        }
      }
    }, slowRequestThreshold);

    slowRequestTimers.value.set(requestId, timer);
  };

  /**
   * Handles successful response and cleans up tracking
   */
  const handleResponseSuccess = (response: AxiosResponse) => {
    const requestId = `${response.config.method}-${response.config.url}`;

    // Clear the timer
    const timer = slowRequestTimers.value.get(requestId);
    if (timer) {
      clearTimeout(timer);
      slowRequestTimers.value.delete(requestId);
    }

    // Remove from active requests
    activeRequests.value.delete(requestId);

    // Clear slow request toast if all requests are done
    if (activeRequests.value.size === 0 && slowRequestToastId.value) {
      toastService.remove(slowRequestToastId.value);
      slowRequestToastId.value = null;
    }
  };

  /**
   * Handles request errors, including connection errors
   */
  const handleRequestError = (error: AxiosError) => {
    const { config } = error;

    // Clear request tracking
    if (config) {
      let requestId = `${config.method}-${config.url}`;
      if (config.url.includes(config.baseURL)) {
        requestId = `${config.method}-${config.url.replace(config.baseURL, '/')}`;
      }

      // Clear the timer
      const timer = slowRequestTimers.value.get(requestId);
      if (timer) {
        clearTimeout(timer);
        slowRequestTimers.value.delete(requestId);
      }

      activeRequests.value.delete(requestId);
    }

    // Clear slow request toast if all requests are done
    if (activeRequests.value.size === 0 && slowRequestToastId.value) {
      toastService.remove(slowRequestToastId.value);
      slowRequestToastId.value = null;
    }

    // If the request is cancelled, cleanup the tracking
    if (axios.isCancel(error)) {
      cleanup();
    }

    // if the request is ERR_NETWORK, show a toast
    if (error.code === 'ERR_NETWORK') {
      const appStore = useAppStore();
      toastService.error(
        appStore.t
            ? appStore.t(
                'app.request_tracking.errors.connection.connection_error'
              )
            : 'Connection error',
        appStore.t
          ? appStore.t(
              'app.request_tracking.errors.connection.connection_error_detail'
            )
          : 'Please check your internet connection and try again.',
        {
          duration: 5000,
          close: false,
        }
      );
      return;
    }

    // Check if this is a connection error
    if (isConnectionError(error)) {
      const appStore = useAppStore();

      // Set detailed error information
      const errorKey = getConnectionErrorKey(error);
      appStore.errorDetails = {
        message: appStore.t
          ? appStore.t(errorKey)
          : 'Unable to connect to the server',
        type: error.code || 'CONNECTION_ERROR',
      };

      // Clear any existing slow request toast since we're showing the error screen
      if (slowRequestToastId.value) {
        toastService.remove(slowRequestToastId.value);
        slowRequestToastId.value = null;
      }

      handleError(error.code, error);
    }
  };

  /**
   * Cleanup function to clear all timers
   */
  const cleanup = () => {
    // Clear all timers
    slowRequestTimers.value.forEach((timer) => clearTimeout(timer));
    slowRequestTimers.value.clear();

    // Clear toast if exists
    if (slowRequestToastId.value) {
      toastService.remove(slowRequestToastId.value);
      slowRequestToastId.value = null;
    }

    // Clear active requests
    activeRequests.value.clear();
  };

  return {
    trackRequestStart,
    handleResponseSuccess,
    handleRequestError,
    cleanup,
    activeRequests,
    slowRequestToastId,
  };
}
